import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Button,
  Typography,
  GlobalStyles,
} from "@mui/material";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import { Formik } from "formik";
import { useTheme } from "@emotion/react";
import { tokens } from "../../theme";
import useMediaQuery from "@mui/material/useMediaQuery";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import Headeer from "../../components/Headeer";
import PatientService from "../../services/doctorServices/patientService";
import FileService from "../../services/doctorServices/FileService";
import ModernDatePicker from "../../components/ModernDatePicker";
import DateOfBirthPicker from "../../components/DateOfBirthPicker";
import WarningSnackbar from "../../components/SnackbarComponent";
import { translateBackendError } from "../../utils/errorTranslator";

export default function AddPatient() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { t } = useTranslation('addPatient');
  const isNonMobile = useMediaQuery("(min-width:600px)");
  const [openSnackBar, setOpenSnackBar] = useState(false);
  const [errorSnackbar, setErrorSnackbar] = useState({ open: false, message: "" });
  const [files, setFiles] = useState([]);
  const [treatmentStartDates, setTreatmentStartDates] = useState({});
  const fileService = new FileService();
  const handleCloseSnackBar = () => {
    setOpenSnackBar(false);
  };
  const handleCloseErrorSnackbar = () => {
    setErrorSnackbar({ open: false, message: "" });
  };

  // Bugünün tarihini YYYY-MM-DD formatında al (minimum tarih için)
  const getTodayDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // 1 ay öncesinin tarihini YYYY-MM-DD formatında al (minimum tarih için)
  const getOneMonthAgoDate = () => {
    const oneMonthAgo = new Date();
    oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
    const year = oneMonthAgo.getFullYear();
    const month = String(oneMonthAgo.getMonth() + 1).padStart(2, '0');
    const day = String(oneMonthAgo.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const initialValues = {
    firstName: "",
    lastName: "",
    gender: "",
    dateOfBirth: "",
    phoneOne: "",
    phoneTwo: "",
    email: "",
    files: [],
    weight: "",
    height: "",
    occupation: "",
  };

  // Create validation schema with translations
  const getSignupSchema = () => Yup.object().shape({
    firstName: Yup.string().required(t('thisFieldRequired')),
    lastName: Yup.string().required(t('thisFieldRequired')),
    gender: Yup.string().required(t('thisFieldRequired')),
    dateOfBirth: Yup.date()
      .required(t('thisFieldRequired'))
      .max(new Date(), t('dateOfBirthCannotBeFuture'))
      .nullable(),
    weight: Yup.number()
      .nullable()
      .optional()
      .positive(t('weightMustBePositive'))
      .max(500, t('weightSeemsTooHigh')),
    height: Yup.number()
      .nullable()
      .optional()
      .positive(t('heightMustBePositive'))
      .max(300, t('heightSeemsTooHigh')),
    occupation: Yup.string()
      .nullable()
      .optional(),
    phoneOne: Yup.string()
      .required(t('thisFieldRequired'))
      .matches(/^\+?[1-9]\d{1,14}$/, t('validPhoneNumber')),
    phoneTwo: Yup.string()
      .nullable()
      .optional()
      .test('valid-phone-if-present', t('validPhoneNumber'), (value) => !value || value === '' || /^\+?[1-9]\d{1,14}$/.test(value)),
    files: Yup.array()
      .nullable()
      .optional()
      .default([]),
    email: Yup.string()
      .required(t('thisFieldRequired'))
      .email(t('invalidEmailFormat')),
  });

  const patientService = new PatientService();

  // Capitalize first letter of each word
  const capitalizeWords = (str) => {
    if (!str) return str;
    // Önce tüm string'i küçük harfe çevir, sonra kelime başlarını büyük yap
    return str.trim().toLowerCase().replace(/(^|\s)\S/g, (match) => match.toUpperCase());
  };

  // Handle blur for firstName and lastName to capitalize
  const handleNameBlur = (fieldName, handleBlur, setFieldValue, values) => {
    handleBlur(fieldName);
    const fieldValue = values[fieldName];
    if (fieldValue && fieldValue.trim()) {
      const capitalized = capitalizeWords(fieldValue);
      if (capitalized !== fieldValue) {
        setFieldValue(fieldName, capitalized);
      }
    }
  };

  const clickToAddPatient = (values, { resetForm, setSubmitting }) => {
    const selectedFiles = files.filter((f) => (values.files || []).includes(f.name));
    const fileIds = selectedFiles.map((f) => f.id);

    // Validate treatment start dates
    if (fileIds.length > 0) {
      const missingDates = [];
      const pastDates = [];
      const oneMonthAgo = getOneMonthAgoDate();
      
      fileIds.forEach((fileId) => {
        const date = treatmentStartDates[fileId];
        const file = files.find((f) => f.id === fileId);
        
        if (!date || date.trim() === "") {
          missingDates.push(file?.name || fileId);
        } else if (date < oneMonthAgo) {
          pastDates.push(file?.name || fileId);
        }
      });
      
      if (missingDates.length > 0) {
        setErrorSnackbar({
          open: true,
          message: t('enterTreatmentStartDateForFiles', { files: missingDates.join(", ") })
        });
        setSubmitting(false);
        return;
      }
      
      if (pastDates.length > 0) {
        setErrorSnackbar({
          open: true,
          message: t('treatmentStartDateCannotBePast', { files: pastDates.join(", ") })
        });
        setSubmitting(false);
        return;
      }
    }

    // Build treatmentStartDates object
    const treatmentStartDatesObj = {};
    fileIds.forEach((fileId) => {
      if (treatmentStartDates[fileId]) {
        treatmentStartDatesObj[fileId] = treatmentStartDates[fileId];
      }
    });

    const patientData = {
      firstName: values.firstName,
      lastName: values.lastName,
      email: values.email,
      gender: values.gender,
      primaryPhone: values.phoneOne,
      secondaryPhone: values.phoneTwo?.trim() || null,
      fileIds: fileIds,
      dateOfBirth: values.dateOfBirth,
      weight: values.weight || null,
      height: values.height || null,
      occupation: values.occupation || null,
      treatmentStartDates: treatmentStartDatesObj,
    };
    debugger;

    const token = localStorage.getItem("token");

    if (!token) {
      setErrorSnackbar({
        open: true,
        message: t('authenticationTokenNotFound')
      });
      setSubmitting(false);
      return;
    }

    return patientService
      .addPatient(patientData, token)
      .then((response) => {
        setOpenSnackBar(true);
        console.log("Patient successfully registered:", response.data);
        debugger;
        resetForm(); // formu sıfırlıyoruz
        setTreatmentStartDates({}); // treatment start dates'i de sıfırla
      })
      .catch((error) => {
        console.error(
          "Error registering patient:",
          error.response?.data || error.message
        );
        if (error.response?.status === 403) {
          return;
        }
        // Backend'den gelen hata anahtarını çevir
        const errorResponse = error.response?.data;
        let errorMessage;
        
        if (errorResponse) {
          // Backend'den gelen error key'ini çevir (fileId gibi parametreler otomatik olarak alınır)
          errorMessage = translateBackendError(errorResponse);
        } else {
          // Eğer error.response?.data yoksa, error.message'ı kullan veya fallback
          errorMessage = error.message || t('failedToRegisterPatient');
        }
        
        setErrorSnackbar({
          open: true,
          message: errorMessage
        });
      })
      .finally(() => {
        setSubmitting(false); // submitting durumunu false yapıyoruz
      });
  };

  useState(() => {
    const fetchAllPatients = async () => {
      try {
        const response = await fileService.getAllFiles();
        console.log("Files getirildi: ", response);
        setFiles(response);
        debugger;
      } catch (err) {
        console.error("Sorun oluştu:", err);
      }
    };
    fetchAllPatients();
  });
  return (
    <Box sx={{ marginLeft: 2, marginRight: 2 }}>
      <GlobalStyles
        styles={{
          ".PhoneInputCountrySelectDropdown": {
            backgroundColor: `${colors.primary[600]} !important`,
            border: `2px solid ${colors.grey[700]} !important`,
            borderRadius: "12px !important",
            boxShadow: `0 8px 32px rgba(0, 0, 0, 0.4) !important`,
            padding: "8px !important",
            maxHeight: "300px !important",
            overflowY: "auto !important",
            "&::-webkit-scrollbar": {
              width: "8px",
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: colors.primary[800],
              borderRadius: "10px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: colors.greenAccent[600],
              borderRadius: "10px",
              "&:hover": {
                backgroundColor: colors.greenAccent[500],
              }
            }
          },
          ".PhoneInputCountryOption": {
            padding: "12px 16px !important",
            borderRadius: "8px !important",
            marginBottom: "4px !important",
            cursor: "pointer !important",
            transition: "all 0.2s !important",
            display: "flex !important",
            alignItems: "center !important",
            gap: "12px !important",
            color: `${colors.grey[100]} !important`,
            "&:hover": {
              backgroundColor: `${colors.greenAccent[700]} !important`,
              transform: "translateX(4px) !important",
            },
            "&--selected": {
              backgroundColor: `${colors.greenAccent[800]} !important`,
              fontWeight: "bold !important",
            }
          },
          ".PhoneInputCountryIcon": {
            borderRadius: "4px !important",
            border: `1px solid ${colors.grey[600]} !important`,
            boxShadow: `0 2px 4px rgba(0, 0, 0, 0.2) !important`,
          },
          // Date Picker Styling
          "input[type='date']::-webkit-calendar-picker-indicator": {
            cursor: "pointer",
            filter: "invert(0.8)",
            opacity: 0.7,
            transition: "all 0.3s ease",
            width: "24px",
            height: "24px",
            borderRadius: "4px",
            padding: "4px",
          },
          "input[type='date']::-webkit-calendar-picker-indicator:hover": {
            opacity: 1,
            transform: "scale(1.1)",
            filter: "invert(1)",
            backgroundColor: `${colors.greenAccent[600]} !important`,
          },
          // Modern Calendar Picker
          "input[type='date']": {
            position: "relative",
          },
          "input[type='date']::-webkit-datetime-edit": {
            color: `${colors.grey[100]} !important`,
            fontSize: "1rem",
            fontWeight: "500",
          },
          "input[type='date']::-webkit-datetime-edit-fields-wrapper": {
            padding: "4px",
          },
          "input[type='date']::-webkit-datetime-edit-text": {
            color: `${colors.grey[400]} !important`,
            padding: "0 4px",
          },
          "input[type='date']::-webkit-datetime-edit-month-field": {
            color: `${colors.grey[100]} !important`,
          },
          "input[type='date']::-webkit-datetime-edit-day-field": {
            color: `${colors.grey[100]} !important`,
          },
          "input[type='date']::-webkit-datetime-edit-year-field": {
            color: `${colors.grey[100]} !important`,
          },
          "input[type='date']::-webkit-inner-spin-button": {
            opacity: 0,
          },
          "input[type='date']::-webkit-clear-button": {
            opacity: 0.5,
            cursor: "pointer",
            filter: "invert(0.5)",
            "&:hover": {
              opacity: 1,
              filter: "invert(1)",
            }
          },
          // Modern Date Picker Popup Styling - Enhanced
          "input[type='date']::-webkit-calendar-picker-dropdown": {
            backgroundColor: `${colors.primary[600]} !important`,
            color: `${colors.grey[100]} !important`,
            border: `2px solid ${colors.grey[700]} !important`,
            borderRadius: "16px !important",
            boxShadow: `0 12px 48px rgba(0, 0, 0, 0.5) !important`,
            padding: "20px !important",
            backdropFilter: "blur(10px) !important",
          },
          // Date Picker Header Container
          "input[type='date']::-webkit-calendar-picker-dropdown-header": {
            backgroundColor: `${colors.primary[700]} !important`,
            padding: "16px !important",
            borderRadius: "12px !important",
            marginBottom: "16px !important",
            display: "flex !important",
            justifyContent: "space-between !important",
            alignItems: "center !important",
          },
          // Date Picker Month/Year Selector
          "input[type='date']::-webkit-calendar-picker-dropdown-month": {
            backgroundColor: `${colors.primary[600]} !important`,
            color: `${colors.grey[100]} !important`,
            borderRadius: "8px !important",
            padding: "10px 16px !important",
            fontWeight: "bold !important",
            fontSize: "1.1rem !important",
            border: `2px solid ${colors.grey[600]} !important`,
            cursor: "pointer !important",
            transition: "all 0.3s !important",
            "&:hover": {
              backgroundColor: `${colors.greenAccent[700]} !important`,
              borderColor: `${colors.greenAccent[500]} !important`,
              transform: "scale(1.05) !important",
              boxShadow: `0 4px 12px ${colors.greenAccent[600]}66 !important`,
            }
          },
          "input[type='date']::-webkit-calendar-picker-dropdown-year": {
            backgroundColor: `${colors.primary[600]} !important`,
            color: `${colors.grey[100]} !important`,
            borderRadius: "8px !important",
            padding: "10px 16px !important",
            fontWeight: "bold !important",
            fontSize: "1.1rem !important",
            border: `2px solid ${colors.grey[600]} !important`,
            cursor: "pointer !important",
            transition: "all 0.3s !important",
            "&:hover": {
              backgroundColor: `${colors.greenAccent[700]} !important`,
              borderColor: `${colors.greenAccent[500]} !important`,
              transform: "scale(1.05) !important",
              boxShadow: `0 4px 12px ${colors.greenAccent[600]}66 !important`,
            }
          },
          // Date Picker Navigation Arrows - Enhanced
          "input[type='date']::-webkit-calendar-picker-dropdown-prev": {
            backgroundColor: `${colors.greenAccent[600]} !important`,
            borderRadius: "50% !important",
            width: "36px !important",
            height: "36px !important",
            transition: "all 0.3s ease !important",
            cursor: "pointer !important",
            display: "flex !important",
            alignItems: "center !important",
            justifyContent: "center !important",
            boxShadow: `0 2px 8px ${colors.greenAccent[600]}66 !important`,
            "&:hover": {
              backgroundColor: `${colors.greenAccent[500]} !important`,
              transform: "scale(1.15) translateX(-2px) !important",
              boxShadow: `0 4px 16px ${colors.greenAccent[500]}99 !important`,
            },
            "&:active": {
              transform: "scale(1.05) !important",
            }
          },
          "input[type='date']::-webkit-calendar-picker-dropdown-next": {
            backgroundColor: `${colors.greenAccent[600]} !important`,
            borderRadius: "50% !important",
            width: "36px !important",
            height: "36px !important",
            transition: "all 0.3s ease !important",
            cursor: "pointer !important",
            display: "flex !important",
            alignItems: "center !important",
            justifyContent: "center !important",
            boxShadow: `0 2px 8px ${colors.greenAccent[600]}66 !important`,
            "&:hover": {
              backgroundColor: `${colors.greenAccent[500]} !important`,
              transform: "scale(1.15) translateX(2px) !important",
              boxShadow: `0 4px 16px ${colors.greenAccent[500]}99 !important`,
            },
            "&:active": {
              transform: "scale(1.05) !important",
            }
          },
          // Date Picker Grid Container
          "input[type='date']::-webkit-calendar-picker-dropdown-grid": {
            display: "grid !important",
            gridTemplateColumns: "repeat(7, 1fr) !important",
            gap: "8px !important",
            padding: "12px !important",
            backgroundColor: `${colors.primary[700]} !important`,
            borderRadius: "12px !important",
          },
          // Date Picker Grid Cells - Enhanced
          "input[type='date']::-webkit-calendar-picker-dropdown-day": {
            backgroundColor: `${colors.primary[500]} !important`,
            color: `${colors.grey[100]} !important`,
            borderRadius: "8px !important",
            padding: "12px !important",
            margin: "0 !important",
            border: `2px solid ${colors.grey[700]} !important`,
            fontWeight: "500 !important",
            fontSize: "0.95rem !important",
            transition: "all 0.2s ease !important",
            cursor: "pointer !important",
            display: "flex !important",
            alignItems: "center !important",
            justifyContent: "center !important",
            minHeight: "40px !important",
            "&:hover": {
              backgroundColor: `${colors.greenAccent[700]} !important`,
              transform: "scale(1.08) !important",
              boxShadow: `0 4px 12px ${colors.greenAccent[600]}66 !important`,
              borderColor: `${colors.greenAccent[500]} !important`,
            }
          },
          "input[type='date']::-webkit-calendar-picker-dropdown-day-selected": {
            backgroundColor: `${colors.greenAccent[600]} !important`,
            color: `${colors.grey[100]} !important`,
            fontWeight: "bold !important",
            fontSize: "1rem !important",
            boxShadow: `0 4px 16px ${colors.greenAccent[600]}99 !important`,
            border: `2px solid ${colors.greenAccent[400]} !important`,
            transform: "scale(1.05) !important",
          },
          "input[type='date']::-webkit-calendar-picker-dropdown-day-today": {
            border: `3px solid ${colors.greenAccent[500]} !important`,
            fontWeight: "bold !important",
            backgroundColor: `${colors.primary[600]} !important`,
            boxShadow: `0 0 0 2px ${colors.greenAccent[500]}33 !important`,
          },
          // Weekday Headers - Enhanced
          "input[type='date']::-webkit-calendar-picker-dropdown-weekday": {
            backgroundColor: `${colors.primary[700]} !important`,
            color: `${colors.grey[300]} !important`,
            fontWeight: "bold !important",
            fontSize: "0.9rem !important",
            padding: "10px !important",
            borderRadius: "6px !important",
            textAlign: "center !important",
            border: `1px solid ${colors.grey[600]} !important`,
          },
          // Other Month Days (Disabled)
          "input[type='date']::-webkit-calendar-picker-dropdown-day-other-month": {
            color: `${colors.grey[500]} !important`,
            opacity: "0.5 !important",
            cursor: "not-allowed !important",
            "&:hover": {
              backgroundColor: `${colors.primary[700]} !important`,
              transform: "none !important",
            }
          },
          // Disabled Days
          "input[type='date']::-webkit-calendar-picker-dropdown-day-disabled": {
            color: `${colors.grey[600]} !important`,
            opacity: "0.3 !important",
            cursor: "not-allowed !important",
            backgroundColor: `${colors.primary[800]} !important`,
            "&:hover": {
              backgroundColor: `${colors.primary[800]} !important`,
              transform: "none !important",
            }
          },
          // Modern Select Dropdown Styling
          ".MuiPopover-root": {
            "& .MuiPaper-root": {
              backgroundColor: `${colors.primary[600]} !important`,
              border: `2px solid ${colors.grey[700]} !important`,
              borderRadius: "12px !important",
              boxShadow: `0 8px 32px rgba(0, 0, 0, 0.4) !important`,
              marginTop: "8px !important",
              maxHeight: "300px !important",
              "&::-webkit-scrollbar": {
                width: "8px",
              },
              "&::-webkit-scrollbar-track": {
                backgroundColor: colors.primary[800],
                borderRadius: "10px",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: colors.greenAccent[600],
                borderRadius: "10px",
                "&:hover": {
                  backgroundColor: colors.greenAccent[500],
                }
              }
            }
          },
          // Menu Item Styling
          ".MuiMenuItem-root": {
            padding: "12px 16px !important",
            borderRadius: "8px !important",
            margin: "4px 8px !important",
            transition: "all 0.2s !important",
            color: `${colors.grey[100]} !important`,
            fontSize: "0.95rem !important",
            fontWeight: "500 !important",
            "&:hover": {
              backgroundColor: `${colors.greenAccent[700]} !important`,
              transform: "translateX(4px) !important",
              boxShadow: `0 2px 8px ${colors.greenAccent[600]}66 !important`,
            },
            "&.Mui-selected": {
              backgroundColor: `${colors.greenAccent[800]} !important`,
              fontWeight: "bold !important",
              "&:hover": {
                backgroundColor: `${colors.greenAccent[700]} !important`,
              }
            },
            "&.Mui-focusVisible": {
              backgroundColor: `${colors.greenAccent[700]} !important`,
            }
          },
          // Select Input Styling
          ".MuiSelect-select": {
            color: `${colors.grey[100]} !important`,
            fontSize: "1rem !important",
            fontWeight: "500 !important",
            padding: "16px !important",
          },
          ".MuiInputLabel-root": {
            color: `${colors.grey[300]} !important`,
            fontSize: "0.875rem !important",
            fontWeight: "500 !important",
            "&.Mui-focused": {
              color: `${colors.greenAccent[400]} !important`,
            },
            "&.MuiInputLabel-shrink": {
              color: `${colors.grey[300]} !important`,
            }
          },
          ".MuiFilledInput-root": {
            "&::before": {
              borderBottom: `2px solid ${colors.grey[700]} !important`,
            },
            "&:hover::before": {
              borderBottom: `2px solid ${colors.greenAccent[500]} !important`,
            },
            "&.Mui-focused::before": {
              borderBottom: `2px solid ${colors.greenAccent[500]} !important`,
            },
            "&::after": {
              borderBottom: `2px solid ${colors.greenAccent[500]} !important`,
            }
          },
          // Select Icon Styling
          ".MuiSelect-icon": {
            color: `${colors.grey[300]} !important`,
            fontSize: "1.5rem !important",
            transition: "all 0.3s !important",
          },
          ".MuiSelect-iconOpen": {
            color: `${colors.greenAccent[400]} !important`,
            transform: "rotate(180deg) !important",
          }
        }}
      />
      <Headeer title={t('title')} subtitle={t('subtitle')} />
      <Box
        sx={{
          mt: 3,
          p: 4,
          borderRadius: 4,
          boxShadow: 15,
          backgroundColor: colors.primary[400],
          border: `2px solid ${colors.primary[100]}`,
          borderTop: `4px solid ${colors.greenAccent[400]}`,
        }}
      >
        <Formik
          initialValues={initialValues}
          validationSchema={getSignupSchema()}
          onSubmit={(values, { resetForm, setSubmitting }) => {
            clickToAddPatient(values, { resetForm, setSubmitting });
          }}
        >
          {({
            values,
            errors,
            touched,
            handleBlur,
            handleChange,
            handleSubmit,
            setFieldValue,
            isSubmitting,
          }) => (
            <form onSubmit={handleSubmit}>
              <Box
                display="grid"
                gap="30px"
                gridTemplateColumns="repeat(4, minmax(0, 1fr))"
                sx={{
                  "& > div": { gridColumn: isNonMobile ? undefined : "span 4" },
                }}
              >
              <Box sx={{ gridColumn: "span 2", display: "flex", flexDirection: "column" }}>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: colors.grey[300],
                    mb: 1,
                    display: "block",
                    fontWeight: "500",
                    fontSize: "0.875rem"
                  }}
                >
                  👤 {t('firstName')}
                </Typography>
                <TextField
                  fullWidth
                  variant="filled"
                  name="firstName"
                  value={values.firstName}
                  onBlur={() => handleNameBlur("firstName", handleBlur, setFieldValue, values)}
                  onChange={handleChange}
                  error={Boolean(touched.firstName && errors.firstName)}
                  helperText={touched.firstName && errors.firstName}
                  placeholder={t('enterFirstName')}
                  sx={{ 
                    "& .MuiFilledInput-root": {
                      backgroundColor: colors.primary[500],
                      "&:hover": {
                        backgroundColor: colors.primary[400],
                      },
                      "&.Mui-focused": {
                        backgroundColor: colors.primary[400],
                        borderColor: colors.greenAccent[500],
                      }
                    },
                    "& .MuiInputLabel-root": {
                      color: colors.grey[300],
                      "&.Mui-focused": {
                        color: colors.greenAccent[400],
                      }
                    }
                  }}
                />
              </Box>
              <Box sx={{ gridColumn: "span 2", display: "flex", flexDirection: "column" }}>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: colors.grey[300],
                    mb: 1,
                    display: "block",
                    fontWeight: "500",
                    fontSize: "0.875rem"
                  }}
                >
                  👤 {t('lastName')}
                </Typography>
                <TextField
                  fullWidth
                  variant="filled"
                  name="lastName"
                  value={values.lastName}
                  onBlur={() => handleNameBlur("lastName", handleBlur, setFieldValue, values)}
                  onChange={handleChange}
                  error={Boolean(touched.lastName && errors.lastName)}
                  helperText={touched.lastName && errors.lastName}
                  placeholder={t('enterLastName')}
                  sx={{ 
                    "& .MuiFilledInput-root": {
                      backgroundColor: colors.primary[500],
                      "&:hover": {
                        backgroundColor: colors.primary[400],
                      },
                      "&.Mui-focused": {
                        backgroundColor: colors.primary[400],
                        borderColor: colors.greenAccent[500],
                      }
                    },
                    "& .MuiInputLabel-root": {
                      color: colors.grey[300],
                      "&.Mui-focused": {
                        color: colors.greenAccent[400],
                      }
                    }
                  }}
                />
              </Box>
              <Box sx={{ gridColumn: "span 2", display: "flex", flexDirection: "column" }}>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: colors.grey[300],
                    mb: 1,
                    display: "block",
                    fontWeight: "500",
                    fontSize: "0.875rem"
                  }}
                >
                  📧 {t('email')}
                </Typography>
                <TextField
                  fullWidth
                  type="email"
                  variant="filled"
                  name="email"
                  value={values.email}
                  error={Boolean(touched.email && errors.email)}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  helperText={touched.email && errors.email}
                  placeholder={t('enterEmail')}
                  sx={{ 
                    "& .MuiFilledInput-root": {
                      backgroundColor: colors.primary[500],
                      "&:hover": {
                        backgroundColor: colors.primary[400],
                      },
                      "&.Mui-focused": {
                        backgroundColor: colors.primary[400],
                        borderColor: colors.greenAccent[500],
                      }
                    },
                    "& .MuiInputLabel-root": {
                      color: colors.grey[300],
                      "&.Mui-focused": {
                        color: colors.greenAccent[400],
                      }
                    }
                  }}
                />
              </Box>
              <Box sx={{ gridColumn: "span 2", display: "flex", flexDirection: "column" }}>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: colors.grey[300],
                    mb: 1,
                    display: "block",
                    fontWeight: "500",
                    fontSize: "0.875rem"
                  }}
                >
                  📅 {t('dateOfBirth')}
                </Typography>
                <DateOfBirthPicker
                  value={values.dateOfBirth}
                  onChange={(date) => {
                    setFieldValue("dateOfBirth", date);
                  }}
                  placeholder={t('selectDateOfBirth')}
                  error={Boolean(touched.dateOfBirth && errors.dateOfBirth)}
                  helperText={touched.dateOfBirth && errors.dateOfBirth || ""}
                  required
                />
              </Box>
              <Box sx={{ gridColumn: "span 1", display: "flex", flexDirection: "column" }}>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: colors.grey[300],
                    mb: 1,
                    display: "block",
                    fontWeight: "500",
                    fontSize: "0.875rem"
                  }}
                >
                  ⚧️ {t('gender')}
                </Typography>
                <FormControl
                  fullWidth
                  variant="filled"
                  sx={{ 
                    "& .MuiFilledInput-root": {
                      backgroundColor: colors.primary[500],
                      "&:hover": {
                        backgroundColor: colors.primary[400],
                      },
                      "&.Mui-focused": {
                        backgroundColor: colors.primary[400],
                      }
                    },
                    "& .MuiInputLabel-root": {
                      color: colors.grey[300],
                      "&.Mui-focused": {
                        color: colors.greenAccent[400],
                      }
                    }
                  }}
                >
                  <InputLabel>{t('selectGender')}</InputLabel>
                  <Select
                    name="gender"
                    value={values.gender}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    error={Boolean(touched.gender && errors.gender)}
                  >
                    <MenuItem value="Male">{t('male')}</MenuItem>
                    <MenuItem value="Female">{t('female')}</MenuItem>
                  </Select>
                  {touched.gender && errors.gender && (
                    <Box sx={{ color: colors.redAccent[500] }}>
                      {errors.gender}
                    </Box>
                  )}
                </FormControl>
              </Box>
              <Box sx={{ gridColumn: "span 1", display: "flex", flexDirection: "column" }}>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: colors.grey[300],
                    mb: 1,
                    display: "block",
                    fontWeight: "500",
                    fontSize: "0.875rem"
                  }}
                >
                  ⚖️ {t('weight')}
                </Typography>
                <TextField
                  fullWidth
                  type="number"
                  variant="filled"
                  name="weight"
                  value={values.weight}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  error={Boolean(touched.weight && errors.weight)}
                  helperText={touched.weight && errors.weight}
                  placeholder={t('enterWeight')}
                  inputProps={{ step: "0.1", min: "0" }}
                  sx={{ 
                    "& .MuiFilledInput-root": {
                      backgroundColor: colors.primary[500],
                      "&:hover": {
                        backgroundColor: colors.primary[400],
                      },
                      "&.Mui-focused": {
                        backgroundColor: colors.primary[400],
                        borderColor: colors.greenAccent[500],
                      }
                    },
                    "& .MuiInputLabel-root": {
                      color: colors.grey[300],
                      "&.Mui-focused": {
                        color: colors.greenAccent[400],
                      }
                    }
                  }}
                />
              </Box>
              <Box sx={{ gridColumn: "span 1", display: "flex", flexDirection: "column" }}>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: colors.grey[300],
                    mb: 1,
                    display: "block",
                    fontWeight: "500",
                    fontSize: "0.875rem"
                  }}
                >
                  📏 {t('height')}
                </Typography>
                <TextField
                  fullWidth
                  type="number"
                  variant="filled"
                  name="height"
                  value={values.height}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  error={Boolean(touched.height && errors.height)}
                  helperText={touched.height && errors.height}
                  placeholder={t('enterHeight')}
                  inputProps={{ step: "0.1", min: "0" }}
                  sx={{ 
                    "& .MuiFilledInput-root": {
                      backgroundColor: colors.primary[500],
                      "&:hover": {
                        backgroundColor: colors.primary[400],
                      },
                      "&.Mui-focused": {
                        backgroundColor: colors.primary[400],
                        borderColor: colors.greenAccent[500],
                      }
                    },
                    "& .MuiInputLabel-root": {
                      color: colors.grey[300],
                      "&.Mui-focused": {
                        color: colors.greenAccent[400],
                      }
                    }
                  }}
                />
              </Box>
              <Box sx={{ gridColumn: "span 1", display: "flex", flexDirection: "column" }}>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: colors.grey[300],
                    mb: 1,
                    display: "block",
                    fontWeight: "500",
                    fontSize: "0.875rem"
                  }}
                >
                  💼 {t('occupation')}
                </Typography>
                <TextField
                  fullWidth
                  variant="filled"
                  name="occupation"
                  value={values.occupation}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  error={Boolean(touched.occupation && errors.occupation)}
                  helperText={touched.occupation && errors.occupation}
                  placeholder={t('enterOccupation')}
                  sx={{ 
                    "& .MuiFilledInput-root": {
                      backgroundColor: colors.primary[500],
                      "&:hover": {
                        backgroundColor: colors.primary[400],
                      },
                      "&.Mui-focused": {
                        backgroundColor: colors.primary[400],
                        borderColor: colors.greenAccent[500],
                      }
                    },
                    "& .MuiInputLabel-root": {
                      color: colors.grey[300],
                      "&.Mui-focused": {
                        color: colors.greenAccent[400],
                      }
                    }
                  }}
                />
              </Box>

              <Box sx={{ gridColumn: "span 1", display: "flex", flexDirection: "column" }}>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: colors.grey[300],
                    mb: 1,
                    display: "block",
                    fontWeight: "500",
                    fontSize: "0.875rem"
                  }}
                >
                  📞 {t('primaryPhone')}
                </Typography>
                <Box
                  sx={{
                    position: "relative",
                    flex: 1,
                    "& .PhoneInput": {
                      width: "100%",
                      display: "flex",
                      alignItems: "center",
                    },
                    "& .PhoneInputInput": {
                      width: "100%",
                      padding: "16px",
                      borderRadius: "8px",
                      border: `2px solid ${
                        touched.phoneOne && errors.phoneOne
                          ? colors.redAccent[500]
                          : colors.grey[700]
                      }`,
                      backgroundColor: colors.primary[500],
                      color: colors.grey[100],
                      transition: "all 0.3s",
                      fontSize: "1rem",
                      height: "56px",
                      "&:focus": {
                        outline: "none",
                        borderColor: touched.phoneOne && errors.phoneOne 
                          ? colors.redAccent[500] 
                          : colors.greenAccent[500],
                        boxShadow: `0 0 0 3px ${
                          touched.phoneOne && errors.phoneOne
                            ? `${colors.redAccent[500]}33`
                            : `${colors.greenAccent[500]}33`
                        }`,
                      },
                      "&:hover": {
                        borderColor: touched.phoneOne && errors.phoneOne
                          ? colors.redAccent[400]
                          : colors.grey[600],
                      },
                      "&::placeholder": {
                        color: colors.grey[500],
                      }
                    },
                    "& .PhoneInputCountryIcon": {
                      width: "32px",
                      height: "24px",
                      borderRadius: "4px",
                      border: `1px solid ${colors.grey[600]}`,
                    },
                    "& .PhoneInputCountryIcon--border": {
                      border: `1px solid ${colors.grey[600]}`,
                    },
                    "& .PhoneInputCountrySelect": {
                      backgroundColor: colors.primary[600],
                      color: colors.grey[100],
                      border: `2px solid ${colors.grey[700]}`,
                      borderRadius: "8px",
                      padding: "8px 12px",
                      marginRight: "8px",
                      fontSize: "0.95rem",
                      fontWeight: "500",
                      cursor: "pointer",
                      transition: "all 0.3s",
                      height: "56px",
                      display: "flex",
                      alignItems: "center",
                      "&:hover": {
                        backgroundColor: colors.primary[500],
                        borderColor: colors.greenAccent[500],
                      },
                      "&:focus": {
                        outline: "none",
                        borderColor: colors.greenAccent[500],
                        boxShadow: `0 0 0 3px ${colors.greenAccent[500]}33`,
                      }
                    },
                    "& .PhoneInputCountrySelectArrow": {
                      opacity: 0.8,
                      color: colors.grey[300],
                      marginLeft: "4px",
                    }
                  }}
                >
                  <PhoneInput
                    international
                    defaultCountry="TR"
                    value={values.phoneOne}
                    onChange={(value) => setFieldValue("phoneOne", value)}
                    error={touched.phoneOne && errors.phoneOne ? true : undefined}
                    placeholder="+90 555 123 4567"
                  />
                </Box>
                {touched.phoneOne && errors.phoneOne && (
                  <Box sx={{ color: colors.redAccent[500], mt: 0.5, fontSize: "0.75rem" }}>
                    {errors.phoneOne}
                  </Box>
                )}
              </Box>

              <Box sx={{ gridColumn: "span 1", display: "flex", flexDirection: "column" }}>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: colors.grey[300],
                    mb: 1,
                    display: "block",
                    fontWeight: "500",
                    fontSize: "0.875rem"
                  }}
                >
                  📱 {t('secondaryPhone')}
                </Typography>
                <Box
                  sx={{
                    position: "relative",
                    flex: 1,
                    "& .PhoneInput": {
                      width: "100%",
                      display: "flex",
                      alignItems: "center",
                    },
                    "& .PhoneInputInput": {
                      width: "100%",
                      padding: "16px",
                      borderRadius: "8px",
                      border: `2px solid ${
                        touched.phoneTwo && errors.phoneTwo
                          ? colors.redAccent[500]
                          : colors.grey[700]
                      }`,
                      backgroundColor: colors.primary[500],
                      color: colors.grey[100],
                      transition: "all 0.3s",
                      fontSize: "1rem",
                      height: "56px",
                      "&:focus": {
                        outline: "none",
                        borderColor: touched.phoneTwo && errors.phoneTwo 
                          ? colors.redAccent[500] 
                          : colors.greenAccent[500],
                        boxShadow: `0 0 0 3px ${
                          touched.phoneTwo && errors.phoneTwo
                            ? `${colors.redAccent[500]}33`
                            : `${colors.greenAccent[500]}33`
                        }`,
                      },
                      "&:hover": {
                        borderColor: touched.phoneTwo && errors.phoneTwo
                          ? colors.redAccent[400]
                          : colors.grey[600],
                      },
                      "&::placeholder": {
                        color: colors.grey[500],
                      }
                    },
                    "& .PhoneInputCountryIcon": {
                      width: "32px",
                      height: "24px",
                      borderRadius: "4px",
                      border: `1px solid ${colors.grey[600]}`,
                    },
                    "& .PhoneInputCountryIcon--border": {
                      border: `1px solid ${colors.grey[600]}`,
                    },
                    "& .PhoneInputCountrySelect": {
                      backgroundColor: colors.primary[600],
                      color: colors.grey[100],
                      border: `2px solid ${colors.grey[700]}`,
                      borderRadius: "8px",
                      padding: "8px 12px",
                      marginRight: "8px",
                      fontSize: "0.95rem",
                      fontWeight: "500",
                      cursor: "pointer",
                      transition: "all 0.3s",
                      height: "56px",
                      display: "flex",
                      alignItems: "center",
                      "&:hover": {
                        backgroundColor: colors.primary[500],
                        borderColor: colors.greenAccent[500],
                      },
                      "&:focus": {
                        outline: "none",
                        borderColor: colors.greenAccent[500],
                        boxShadow: `0 0 0 3px ${colors.greenAccent[500]}33`,
                      }
                    },
                    "& .PhoneInputCountrySelectArrow": {
                      opacity: 0.8,
                      color: colors.grey[300],
                      marginLeft: "4px",
                    }
                  }}
                >
                  <PhoneInput
                    international
                    defaultCountry="TR"
                    value={values.phoneTwo}
                    onChange={(value) => setFieldValue("phoneTwo", value)}
                    error={touched.phoneTwo && errors.phoneTwo ? true : undefined}
                    placeholder="+90 555 123 4567"
                  />
                </Box>
                {touched.phoneTwo && errors.phoneTwo && (
                  <Box sx={{ color: colors.redAccent[500], mt: 0.5, fontSize: "0.75rem" }}>
                    {errors.phoneTwo}
                  </Box>
                )}
              </Box>

              <Box sx={{ gridColumn: "span 2", display: "flex", flexDirection: "column" }}>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: colors.grey[300],
                    mb: 1,
                    display: "block",
                    fontWeight: "500",
                    fontSize: "0.875rem"
                  }}
                >
                  📁 {t('files')}
                </Typography>
                <FormControl
                  fullWidth
                  variant="filled"
                  sx={{ 
                    "& .MuiFilledInput-root": {
                      backgroundColor: colors.primary[500],
                      "&:hover": {
                        backgroundColor: colors.primary[400],
                      },
                      "&.Mui-focused": {
                        backgroundColor: colors.primary[400],
                      }
                    },
                    "& .MuiInputLabel-root": {
                      color: colors.grey[300],
                      "&.Mui-focused": {
                        color: colors.greenAccent[400],
                      }
                    }
                  }}
                >
                  <InputLabel>{t('selectFiles')}</InputLabel>
                  <Select
                    name="files"
                    multiple
                    value={values.files}
                    onBlur={handleBlur}
                    onChange={(e) => {
                      handleChange(e);
                      // Update treatmentStartDates when files change
                      const newSelectedFiles = files.filter((f) => e.target.value.includes(f.name));
                      const newFileIds = newSelectedFiles.map((f) => f.id);
                      const newTreatmentDates = { ...treatmentStartDates };
                      
                      // Remove dates for unselected files
                      Object.keys(newTreatmentDates).forEach((fileId) => {
                        if (!newFileIds.includes(fileId)) {
                          delete newTreatmentDates[fileId];
                        }
                      });
                      
                      // Add empty dates for newly selected files
                      newFileIds.forEach((fileId) => {
                        if (!newTreatmentDates[fileId]) {
                          newTreatmentDates[fileId] = "";
                        }
                      });
                      
                      setTreatmentStartDates(newTreatmentDates);
                    }}
                    error={Boolean(touched.files && errors.files)}
                    renderValue={(selected) => (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {selected.map((value, idx) => (
                          <Box
                            key={idx}
                            sx={{
                              backgroundColor: colors.blueAccent[600],
                              padding: "1px 8px",
                              borderRadius: "16px",
                              color: "white",
                              fontSize: "0.8rem",
                            }}
                          >
                            {value}
                          </Box>
                        ))}
                      </Box>
                    )}
                  >
                    {files.map((file, index) => (
                      <MenuItem 
                        key={index} 
                        value={file?.name || ""}
                        sx={{
                          "&:hover": {
                            backgroundColor: colors.greenAccent[700],
                            color: "white"
                          }
                        }}
                      >
                        {file?.name || ""}
                      </MenuItem>
                    ))}
                  </Select>
                  {touched.files && errors.files && (
                    <Box sx={{ color: colors.redAccent[500] }}>
                      {errors.files}
                    </Box>
                  )}
                </FormControl>
              </Box>

              {/* Treatment Start Dates Section */}
              {values.files && values.files.length > 0 && (
                <Box sx={{ gridColumn: "span 4", mt: 2 }}>
                  <Box
                    sx={{
                      p: 3,
                      borderRadius: 2,
                      background: `linear-gradient(135deg, ${colors.blueAccent[800]}30 0%, ${colors.primary[500]} 100%)`,
                      border: `1px solid ${colors.blueAccent[500]}40`,
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        color: colors.grey[100],
                        fontWeight: 600,
                        mb: 2,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      📅 {t('treatmentStartDates')}
                    </Typography>
                    <Box 
                      sx={{ 
                        display: "grid",
                        gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
                        gap: 2
                      }}
                    >
                      {values.files.map((fileName) => {
                        const selectedFile = files.find((f) => f.name === fileName);
                        if (!selectedFile) return null;
                        const fileId = selectedFile.id;
                        
                        return (
                          <Box key={fileId} sx={{ gridColumn: "span 2" }}>
                            <Typography
                              variant="body2"
                              sx={{
                                color: colors.grey[300],
                                mb: 1,
                                fontWeight: 500,
                              }}
                            >
                              {fileName}
                            </Typography>
                            <ModernDatePicker
                              value={treatmentStartDates[fileId] || ""}
                              onChange={(selectedDate) => {
                                const oneMonthAgo = getOneMonthAgoDate();
                                
                                // Geçmiş tarih kontrolü (1 ay öncesine kadar izin ver)
                                if (selectedDate && selectedDate < oneMonthAgo) {
                                  alert(t('treatmentStartDateCannotBePastAlert'));
                                  return;
                                }
                                
                                setTreatmentStartDates({
                                  ...treatmentStartDates,
                                  [fileId]: selectedDate,
                                });
                              }}
                              minDate={getOneMonthAgoDate()}
                              placeholder={t('selectTreatmentStartDate')}
                              error={!treatmentStartDates[fileId]}
                              helperText={!treatmentStartDates[fileId] ? t('treatmentStartDateRequired') : ""}
                              required
                            />
                            {!treatmentStartDates[fileId] && (
                              <Typography
                                variant="caption"
                                sx={{
                                  color: colors.redAccent[400],
                                  mt: 0.5,
                                  display: "block",
                                }}
                              >
                                {t('treatmentStartDateRequired')}
                              </Typography>
                            )}
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                </Box>
              )}
            </Box>

            <Box 
              marginTop={4} 
              display="flex" 
              justifyContent="end"
              sx={{
                borderTop: `2px solid ${colors.primary[300]}`,
                pt: 3,
                mt: 3
              }}
            >
              <Button
                type="submit"
                disabled={isSubmitting}
                sx={{ 
                  marginRight: 0,
                  background: `linear-gradient(135deg, ${colors.greenAccent[600]} 0%, ${colors.blueAccent[600]} 100%)`,
                  color: "white",
                  fontWeight: "bold",
                  fontSize: "1rem",
                  px: 4,
                  py: 1.5,
                  borderRadius: 2,
                  boxShadow: `0 4px 12px rgba(0, 0, 0, 0.3)`,
                  textTransform: "none",
                  "&:hover": {
                    background: `linear-gradient(135deg, ${colors.greenAccent[700]} 0%, ${colors.blueAccent[700]} 100%)`,
                    transform: "scale(1.05)",
                    boxShadow: `0 6px 16px rgba(0, 0, 0, 0.4)`,
                  },
                  transition: "all 0.3s"
                }}
                variant="contained"
              >
                ➕ {isSubmitting ? t('adding') : t('addNewPatient')}
              </Button>
              <WarningSnackbar
                open={openSnackBar}
                autoHideDuration={3000}
                onClose={handleCloseSnackBar}
                message={`✅ ${t('patientAddedSuccessfully')}`}
                severity="success"
              />
              <WarningSnackbar
                open={errorSnackbar.open}
                autoHideDuration={5000}
                onClose={handleCloseErrorSnackbar}
                message={errorSnackbar.message}
                severity="error"
              />
            </Box>
          </form>
        )}
      </Formik>
      </Box>
    </Box>
  );
}
